/**
 * Meta Pixel for creator recruitment at /join and a confirmed first upload.
 *
 * Scoped to recruitment rather than loaded app-wide. The portal sends only a
 * server-verified first-video receipt. It never sends
 * portal PageViews, invite URLs or authentication URLs.
 *
 * No email, name or phone is passed to the browser Pixel. Automatic matching
 * is explicitly disabled for this integration: the
 * applicant is handing us their details to apply to a program, not to be
 * resolved against an ad profile.
 *
 * That holds for this file and stops here. The server-side
 * `CreatorVideoApproved` event does send a hashed email, deliberately: by the
 * time it fires the person has signed an agreement and been onboarded as a
 * creator, and for anyone whose browser blocked this script there is no cookie
 * to attribute them by at all. See `lib/creatorApplication/metaConversionsApi.ts`
 * in the backend. Do not read the paragraph above as the system-wide stance.
 */

import {
  CREATOR_ACQUISITION_DATASET_ID,
  CREATOR_FUNNEL_EVENTS,
  isMetaClickIdentifier,
  type LEGACY_WEB_APPLIED_EVENT,
} from "@newform/constants";

/**
 * The host the creator ads point at. Previews, personal staging and localhost
 * stay dark.
 *
 * This gate matters more than the usual "don't pollute analytics" instinct:
 * pixel data is cumulative and events feed ad-set optimisation, so a Lead
 * fired from a test run cannot be retracted once Meta has counted it. Set
 * NEXT_PUBLIC_META_PIXEL_ID on a single preview to point it at a test pixel.
 */
const PRODUCTION_HOST = "app.newform.com";

const FBEVENTS_SRC = "https://connect.facebook.net/en_US/fbevents.js";

export type MetaPixelEventName =
  | "PageView"
  | (typeof CREATOR_FUNNEL_EVENTS)["applied"]
  | typeof LEGACY_WEB_APPLIED_EVENT;

type MetaPixelArg =
  | string
  | boolean
  | Record<string, string | number | boolean>;
type MetaPixelCall = MetaPixelArg[];

interface MetaPixelFn {
  (...args: MetaPixelCall): void;
  callMethod?: (...args: MetaPixelCall) => void;
  queue: MetaPixelCall[];
  push: (...args: MetaPixelCall) => void;
  loaded: boolean;
  version: string;
}

const initializedPixels = new WeakMap<MetaPixelFn, Set<string>>();
const loadedScripts = new WeakSet<MetaPixelFn>();
type PendingPixelEvent = {
  args: MetaPixelCall;
  pathname: string;
  key: string;
  expiresAt: number;
  onDispatch?: () => void;
};
const pendingEvents = new WeakMap<MetaPixelFn, PendingPixelEvent[]>();
const MAX_PENDING_EVENT_AGE_MS = 5 * 60 * 1000;

const META_PAGE_PATHS = new Set(["/join", "/join/apply", "/one/submit"]);
const CREDENTIAL_PARAMETER =
  /token|secret|password|email|authorization|session|^(code|otp|auth)$/i;

function hasCredentials(url: URL): boolean {
  return (
    Boolean(url.username || url.password || url.hash) ||
    url.pathname.startsWith("/join/invite/") ||
    [...url.searchParams.keys()].some((key) => CREDENTIAL_PARAMETER.test(key))
  );
}

/** fbq reads the real document and referrer; a CAPI URL override cannot hide them. */
export function canUseCreatorMetaPixel(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const page = new URL(window.location.href);
    return (
      META_PAGE_PATHS.has(page.pathname) &&
      !hasCredentials(page) &&
      (!document.referrer || !hasCredentials(new URL(document.referrer)))
    );
  } catch {
    return false;
  }
}

function sendPixelEvent(
  args: MetaPixelCall,
  key: string,
  onDispatch?: () => void,
  expiresAt = Date.now() + MAX_PENDING_EVENT_AGE_MS,
): boolean {
  if (!canUseCreatorMetaPixel() || !window.fbq || Date.now() >= expiresAt)
    return false;
  const fbq = window.fbq;
  if (fbq.callMethod || loadedScripts.has(fbq)) {
    fbq(...args);
    onDispatch?.();
    return true;
  }
  // The SDK builds document/referrer fields when it drains its queue. Keep
  // events outside that queue so a late load cannot replay them on an auth URL.
  const pending = pendingEvents.get(fbq) ?? [];
  const pathname = window.location.pathname;
  if (
    !pending.some((event) => event.pathname === pathname && event.key === key)
  ) {
    pending.push({ args, pathname, key, onDispatch, expiresAt });
    pendingEvents.set(fbq, pending);
  }
  return true;
}

function flushPixelEvents(fbq: MetaPixelFn): void {
  loadedScripts.add(fbq);
  const pending = pendingEvents.get(fbq) ?? [];
  pendingEvents.delete(fbq);
  for (const event of pending) {
    if (
      !canUseCreatorMetaPixel() ||
      window.location.pathname !== event.pathname ||
      Date.now() >= event.expiresAt
    ) {
      continue;
    }
    fbq(...event.args);
    event.onDispatch?.();
  }
}

declare global {
  interface Window {
    fbq?: MetaPixelFn;
    _fbq?: MetaPixelFn;
  }
}

/**
 * Returns the pixel to load for this hostname, or null to stay dark.
 *
 * Split out from the component so the host gate is unit-testable — it is the
 * one piece of this file whose failure mode is silent and irreversible.
 *
 * Production is resolved first and cannot be overridden. The override exists
 * to point a preview at a test pixel, and it has no legitimate use on the real
 * host — so letting it win there would only ever mean an environment variable
 * set globally instead of branch-scoped silently diverting live creator Leads
 * to somebody's test pixel. Same unretractable failure as firing test events
 * at the real one, just in the other direction.
 */
export function resolveMetaPixelId(hostname: string): string | null {
  if (hostname === PRODUCTION_HOST) return CREATOR_ACQUISITION_DATASET_ID;
  return process.env.NEXT_PUBLIC_META_PIXEL_ID?.trim() || null;
}

/**
 * Meta's queue shim, written out in TypeScript rather than injected as an
 * opaque string. Initialization calls made before fbevents.js arrives land in
 * its queue. Events wait separately until the script loads on the same safe
 * page, rather than inheriting a later authentication URL during replay.
 */
function ensureFbq(): MetaPixelFn {
  const existing = window.fbq;
  if (existing) return existing;

  const fbq = ((...args: MetaPixelCall): void => {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
      return;
    }
    fbq.queue.push(args);
  }) as MetaPixelFn;

  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];

  window.fbq = fbq;
  window._fbq = fbq;
  return fbq;
}

export function initMetaPixel(pixelId: string): void {
  const fbq = ensureFbq();
  const initialized = initializedPixels.get(fbq) ?? new Set<string>();
  if (!initialized.has(pixelId)) {
    // These controls affect this recruitment integration, not the shared
    // dataset. Meta's published fbevents.js supports the scoped optOut command;
    // autoConfig's AutomaticSetup bundle alone does not include base matching.
    fbq("set", "autoConfig", false, pixelId);
    fbq("optOut", pixelId, "AutomaticMatching");
    fbq("init", pixelId);
    initialized.add(pixelId);
    initializedPixels.set(fbq, initialized);
  }

  const existing = document.querySelector(`script[src="${FBEVENTS_SRC}"]`);
  if (existing) {
    existing.addEventListener("load", () => flushPixelEvents(fbq), {
      once: true,
    });
    return;
  }
  const script = document.createElement("script");
  script.async = true;
  script.src = FBEVENTS_SRC;
  script.addEventListener("load", () => flushPixelEvents(fbq), { once: true });
  document.head.appendChild(script);
}

/**
 * `eventId` is what Meta pairs a browser event with the server's copy on
 * (`fbq`'s fourth argument), so the two count once. The server sends a copy of
 * `SubmitApplication`; see `creatorMetaEvents.ts` in the backend.
 */
export function trackMetaPixelEvent(
  event: MetaPixelEventName,
  { eventId }: { eventId?: string } = {},
): void {
  if (eventId) {
    sendPixelEvent(["track", event, {}, { eventID: eventId }], eventId);
    return;
  }
  sendPixelEvent(["track", event], event);
}

export function trackFirstVideoSubmissionPixel(
  eventId: string,
  onDispatch?: () => void,
  expiresAt?: number,
): boolean {
  if (!canUseCreatorMetaPixel()) return false;
  const pixelId = resolveMetaPixelId(window.location.hostname);
  if (!pixelId) return false;
  initMetaPixel(pixelId);
  return sendPixelEvent(
    [
      "trackSingleCustom",
      pixelId,
      CREATOR_FUNNEL_EVENTS.firstVideoSubmitted,
      {},
      { eventID: eventId },
    ],
    eventId,
    onDispatch,
    expiresAt,
  );
}

/**
 * The pixel's own click identifiers, for a conversion that happens later.
 *
 * `_fbp` identifies the browser the pixel saw; `_fbc` carries the `fbclid`
 * from the ad click that brought them here. The landing capture also preserves
 * fbc from a real fbclid before a fast navigation can lose it. No synthetic
 * click identifier is created for someone who never arrived from an ad.
 *
 * Read at submit and sent with the application because the conversion worth
 * optimising this funnel on — an applicant whose first video gets approved —
 * is decided by staff days later, with no browser to fire an event from. The
 * server sends that one through the Conversions API, and these are the only
 * things that can tie it back to the ad. They cannot be recovered afterwards,
 * so if they are not captured here they are gone.
 *
 * Note this is the same pair the browser pixel already attaches to every event
 * it fires, which is what keeps it inside the no-advanced-matching line drawn
 * at the top of this file: no email, name or phone goes to Meta either way.
 */
export function readMetaClickIdentifiers(): {
  fbp?: string;
  fbc?: string;
} {
  const read = (name: string): string | undefined => {
    for (const entry of document.cookie.split(";")) {
      const separator = entry.indexOf("=");
      // A cookie with no "=" is a bare name with no value, so there is nothing
      // to read from it either way.
      if (separator === -1) continue;
      if (entry.slice(0, separator).trim() !== name) continue;
      const raw = entry.slice(separator + 1).trim();
      // Neither identifier contains a character that needs escaping, so a
      // value that fails to decode is not one of ours — and letting the throw
      // out would take the application submit down with it.
      try {
        const value = decodeURIComponent(raw);
        return isMetaClickIdentifier(value) ? value : undefined;
      } catch {
        return undefined;
      }
    }
    return undefined;
  };

  return { fbp: read("_fbp"), fbc: read("_fbc") };
}
